import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import TutorialPosition from './TutorialPosition.enum';

/**
 *
 * Tutorial tech spec:
 * https://stamps.atlassian.net/wiki/spaces/RnD/pages/460128678/Tutorial+Module#Tip-Object
 *
 * Regarding DOMRect object:
 * https://developer.mozilla.org/en-US/docs/Web/API/DOMRect
 *
 */

export default class Util {
	/**
	 * Important:
	 * This needs to match the className in TutorialModal.
	 */
	static modalCls = '.tutorial-modal';

	/**
	 * Important:
	 * This needs to match the arrow size defined in the theme.
	 * See '.tutorial-modal' selector in modal.scss.
	 */
	static arrowSize = 12;

	/**
	 * Calculate the position of Tutorial modal.
	 *
	 * @param {object} tip See Tip object in the spec.
	 */
	static calculateModalPosition = tip => {
		const { element, position, offset, showArrow } = tip;

		// get DOMRect object of the element
		const elementDOMRect = this._getDOMRect(element);

		// get the DOMRect object of Tutorial modal
		const tutorialModalDOMRect = this._getDOMRect(this.modalCls);

		// check if modal position needs fine-tuning
		const shift = {
			top: isArray(offset) && isNumber(offset[1]) ? offset[1] : 0,
			left: isArray(offset) && isNumber(offset[0]) ? offset[0] : 0,
		};

		// calculate the modal position
		const modalPosition = {
			top: this._calculateTop(
				position,
				elementDOMRect,
				tutorialModalDOMRect,
				shift,
				showArrow
			),
			left: this._calculateLeft(
				position,
				elementDOMRect,
				tutorialModalDOMRect,
				shift,
				showArrow
			),

			// calculate the arrow position relative to the Modal position
			arrow:
				showArrow === true && isObject(elementDOMRect)
					? this._calculateArrowPosition(position, elementDOMRect)
					: null,
		};

		return isNumber(modalPosition.top) && isNumber(modalPosition.left)
			? modalPosition
			: null;
	};

	/**
	 * Get element's DOMRect object.
	 *
	 * @param {string} element A selector string for locating the DOM element.
	 * @returns {DOMRect}
	 */
	static _getDOMRect = selector => {
		const queryResult = isString(selector)
			? document.querySelector(selector)
			: null;

		return queryResult && isFunction(queryResult.getBoundingClientRect)
			? queryResult.getBoundingClientRect()
			: null;
	};

	/**
	 * Calculate modal arrow's position relative its parent top-left corner.
	 *
	 * @param {TutorialPosition|array} pos
	 * @param {DOMRect} elementDOMRect DOMRect object of a DOM element.
	 * @returns {object}
	 */
	static _calculateArrowPosition = (pos, elementDOMRect) => {
		// distance to shift the arrow so that it doesn't hide behind the dialog
		const distanceToReveal = this.arrowSize * -2;

		// distance in x-axis between the arrow tip and the element left edge so that
		// the tip is pointing at the center of the element
		const xAxisDelta = elementDOMRect.width / 2 - this.arrowSize;

		// distance in y-axis between the arrow tip and the element top edge so that
		// the tip is pointing at the center of the element
		const yAxisDelta = elementDOMRect.height / 2 - this.arrowSize;

		// when at the top of a DOM element
		if (
			pos === TutorialPosition.Top ||
			pos === TutorialPosition.TopRightAligned
		) {
			return {
				// the arrow is pointing south
				direction: 'south',
				position:
					pos === TutorialPosition.Top
						? {
								bottom: distanceToReveal,
								left: xAxisDelta,
							}
						: {
								bottom: distanceToReveal,
								right: xAxisDelta,
							},
			};
		}

		// when at the bottom of a DOM element
		if (
			pos === TutorialPosition.Bottom ||
			pos === TutorialPosition.BottomRightAligned
		) {
			return {
				// the arrow is pointing north
				direction: 'north',
				position:
					pos === TutorialPosition.Bottom
						? {
								top: distanceToReveal,
								left: xAxisDelta,
							}
						: {
								top: distanceToReveal,
								right: xAxisDelta,
							},
			};
		}

		// when at the left side of a DOM element
		if (
			pos === TutorialPosition.Left ||
			pos === TutorialPosition.LeftBottomAligned
		) {
			return {
				// the arrow is pointing east
				direction: 'east',
				position:
					pos === TutorialPosition.Left
						? {
								top: yAxisDelta,
								right: distanceToReveal,
							}
						: {
								bottom: yAxisDelta,
								right: distanceToReveal,
							},
			};
		}

		// when at the right side of a DOM element
		if (
			pos === TutorialPosition.Right ||
			pos === TutorialPosition.RightBottomAligned
		) {
			return {
				// the arrow is pointing west
				direction: 'west',
				position:
					pos === TutorialPosition.Right
						? {
								top: yAxisDelta,
								left: distanceToReveal,
							}
						: {
								bottom: yAxisDelta,
								left: distanceToReveal,
							},
			};
		}

		return null;
	};

	/**
	 * Calculate modal's position on the y-axis.
	 *
	 * @param {TutorialPosition|array} position
	 * @param {DOMRect} elementDOMRect DOMRect object of a DOM element.
	 * @param {DOMRect} tutorialModalDOMRect DOMRect object of a TutorialModal element.
	 * @param {object} shift Object containing top and left numeric value.
	 * @returns {number}
	 */
	static _calculateTop = (
		position,
		elementDOMRect,
		tutorialModalDOMRect,
		shift,
		showArrow
	) => {
		const yPos = isArray(position) ? position[1] : position;

		/**
		 *
		 * Positioned next to a DOM element
		 *
		 */
		if (isObject(elementDOMRect)) {
			// at the top of a DOM element
			if (
				yPos === TutorialPosition.Top ||
				yPos === TutorialPosition.TopRightAligned
			) {
				return (
					elementDOMRect.top -
					tutorialModalDOMRect.height +
					shift.top -
					(showArrow ? this.arrowSize : 0)
				);
			}

			// at the bottom of a DOM element
			if (
				yPos === TutorialPosition.Bottom ||
				yPos === TutorialPosition.BottomRightAligned
			) {
				return (
					elementDOMRect.top +
					elementDOMRect.height +
					shift.top +
					(showArrow ? this.arrowSize : 0)
				);
			}

			// at the left (or right) side of a DOM element
			if (yPos === TutorialPosition.Left || yPos === TutorialPosition.Right) {
				return elementDOMRect.top + shift.top;
			}

			// at the left (or left) side of a DOM element, bottom-aligned
			if (
				yPos === TutorialPosition.LeftBottomAligned ||
				yPos === TutorialPosition.RightBottomAligned
			) {
				return (
					elementDOMRect.top +
					elementDOMRect.height -
					tutorialModalDOMRect.height +
					shift.top
				);
			}

			// at a specific coordinate from a DOM element's top-left
			if (isNumber(yPos)) {
				return elementDOMRect.top + yPos + shift.top;
			}
		}

		/**
		 *
		 * Positioned relative to the viewport
		 *
		 */

		// at the top of the viewport
		if (yPos === TutorialPosition.Top) {
			return 0 + shift.top;
		}

		// at the center, left or right of the viewport
		if (
			yPos === TutorialPosition.Center ||
			yPos === TutorialPosition.Left ||
			yPos === TutorialPosition.Right
		) {
			return (window.innerHeight - tutorialModalDOMRect.height) / 2 + shift.top;
		}

		// at the bottom of the viewport
		if (yPos === TutorialPosition.Bottom) {
			return window.innerHeight - tutorialModalDOMRect.height + shift.top;
		}

		// at a specific coordinate from the viewport's top-left
		if (isNumber(yPos)) {
			return yPos + shift.top;
		}

		return null;
	};

	/**
	 * Calculate modal's position on the x-axis.
	 *
	 * @param {TutorialPosition|array} position
	 * @param {DOMRect} elementDOMRect DOMRect object of a DOM element.
	 * @param {DOMRect} tutorialModalDOMRect DOMRect object of a TutorialModal element.
	 * @param {object} shift Object containing top and left numeric value.
	 * @returns {number}
	 */
	static _calculateLeft = (
		position,
		elementDOMRect,
		tutorialModalDOMRect,
		shift,
		showArrow
	) => {
		const xPos = isArray(position) ? position[0] : position;

		/**
		 *
		 * Positioned next to a DOM element
		 *
		 */
		if (isObject(elementDOMRect)) {
			// at the top (or bottom) of a DOM element
			if (xPos === TutorialPosition.Top || xPos === TutorialPosition.Bottom) {
				return elementDOMRect.left + shift.left;
			}

			// at the right side of a DOM element
			if (
				xPos === TutorialPosition.Right ||
				xPos === TutorialPosition.RightBottomAligned
			) {
				return (
					elementDOMRect.left +
					elementDOMRect.width +
					shift.left +
					(showArrow ? this.arrowSize : 0)
				);
			}

			// at the left side of a DOM element
			if (
				xPos === TutorialPosition.Left ||
				xPos === TutorialPosition.LeftBottomAligned
			) {
				return (
					elementDOMRect.left -
					tutorialModalDOMRect.width +
					shift.left -
					(showArrow ? this.arrowSize : 0)
				);
			}

			// at the top (or bottom) of a DOM element, right-aligned
			if (
				xPos === TutorialPosition.TopRightAligned ||
				xPos === TutorialPosition.BottomRightAligned
			) {
				return (
					elementDOMRect.left +
					elementDOMRect.width -
					tutorialModalDOMRect.width +
					shift.left
				);
			}

			// at a specific coordinate from a DOM element's top-left
			if (isNumber(xPos)) {
				return elementDOMRect.left + xPos + shift.left;
			}
		}

		/**
		 *
		 * Positioned relative to the viewport
		 *
		 */

		// at the left side of the viewport
		if (xPos === TutorialPosition.Left) {
			return 0 + shift.top;
		}

		// at the center, top or bottom of the viewport
		if (
			xPos === TutorialPosition.Center ||
			xPos === TutorialPosition.Top ||
			xPos === TutorialPosition.Bottom
		) {
			return (window.innerWidth - tutorialModalDOMRect.width) / 2 + shift.top;
		}

		// at the right side of the viewport
		if (xPos === TutorialPosition.Right) {
			return window.innerWidth - tutorialModalDOMRect.width + shift.top;
		}

		// at a specific coordinate from the viewport's top-left
		if (isNumber(xPos)) {
			return xPos + shift.left;
		}

		return null;
	};
}
